import { useMediaQuery } from "@mui/material";
import type { ModalProps } from "@mui/material/Modal";
import type { PaperProps } from "@mui/material/Paper";
import SwipeableDrawer, {
  type SwipeableDrawerProps,
} from "@mui/material/SwipeableDrawer";
import { useTheme } from "@mui/material/styles";
import { omit } from "lodash";
import React, { useRef, useMemo, forwardRef } from "react";
import { useUser } from "@/auth/useUser";
import { IMPERSONATION_BAR_HEIGHT } from "../topBar/impersonationBar";
import { TOP_BAR_HEIGHT } from "../topBar/topBar";
import DrawerProvider, {
  useDrawerContext,
  type DrawerContextType,
} from "./_drawerContext";

export type DrawerProps = {
  open?: boolean;
  DrawerProps?: Partial<
    Omit<SwipeableDrawerProps, "ModalProps" | "PaperProps" | "anchor">
  >;
  anchor?: SwipeableDrawerProps["anchor"];
  PaperProps?: Partial<PaperProps>;
  ModalProps?: Partial<ModalProps>;
  desktopWidth?: string;
  onClose?: VoidFunction;
  onOpen?: VoidFunction;
};

export type DrawerRef = DrawerContextType;

type DrawerInnerProps = Omit<DrawerProps, "onClose" | "onOpen" | "open">;

const DrawerInner = ({
  DrawerProps,
  ModalProps,
  PaperProps,
  anchor,
  desktopWidth,
  children,
}: React.PropsWithChildren<DrawerInnerProps>) => {
  const { user } = useUser();
  const isImpersonating = !!user?.impersonatedId;
  const theme = useTheme();

  const { close, open, isOpen } = useDrawerContext();

  const swipeableDrawerRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery<typeof theme>((theme) =>
    theme.breakpoints.down("md")
  );

  const _anchor = useMemo(
    () => (anchor ?? isMobile ? "bottom" : "right"),
    [isMobile, anchor]
  );

  const maxSize = useMemo(
    () =>
      isMobile
        ? { maxHeight: "90dvh", minHeight: "30dvh" }
        : {
            maxWidth: desktopWidth ?? "65dvw",
            minWidth: desktopWidth ?? "280px",
          },
    [isMobile, desktopWidth]
  );

  const hideBackdrop = DrawerProps?.hideBackdrop ?? !isMobile;
  const topBarHeight = user ? TOP_BAR_HEIGHT : "0px";
  const impersionationBarHeight = isImpersonating
    ? IMPERSONATION_BAR_HEIGHT
    : "0px";
  const paddingTop = `calc(${topBarHeight} + ${impersionationBarHeight})`;

  return (
    <SwipeableDrawer
      ref={swipeableDrawerRef}
      open={isOpen}
      anchor={_anchor}
      onClose={close}
      onOpen={open}
      disableSwipeToOpen
      disableDiscovery
      allowSwipeInChildren={false}
      hideBackdrop={hideBackdrop}
      {...DrawerProps}
      sx={[
        { zIndex: theme.zIndex.drawer + (hideBackdrop ? 0 : 2) }, // the top bar is at zIndex.drawer + 1
        ...(Array.isArray(DrawerProps?.sx)
          ? DrawerProps.sx
          : [DrawerProps?.sx]),
      ]}
      PaperProps={{
        ...PaperProps,
        sx: [
          {
            ...maxSize,
            boxShadow: "0 10px 24px rgba(22, 22, 22, 0.08)",
            borderTopLeftRadius: {
              xs: theme.spacing(3),
              md: 0,
            },
            borderTopRightRadius: {
              xs: theme.spacing(3),
              md: 0,
            },
            transition: theme.transitions.create("height", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
            pt: hideBackdrop ? paddingTop : 0,
          },
          ...(Array.isArray(PaperProps?.sx) ? PaperProps.sx : [PaperProps?.sx]),
        ],
      }}
      ModalProps={{
        keepMounted: false,
        sx: [
          {
            left: hideBackdrop ? "initial" : 0,
          },
          ...(Array.isArray(ModalProps?.sx) ? ModalProps.sx : [ModalProps?.sx]),
        ],
        ...omit(ModalProps, ["sx"]),
      }}
    >
      {children}
    </SwipeableDrawer>
  );
};

const DrawerImpl: React.ForwardRefRenderFunction<
  DrawerContextType,
  React.PropsWithChildren<DrawerProps>
> = ({ onClose, onOpen, open, ...props }, ref) => (
  <DrawerProvider onClose={onClose} onOpen={onOpen} open={open} ref={ref}>
    <DrawerInner {...props} />
  </DrawerProvider>
);

const Drawer = forwardRef(DrawerImpl);

export default Drawer;
